import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon } from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader';
import { Button, ButtonLink } from '../components/ui/Button';
import { Pagination, Table, TD, TH, THead, TR } from '../components/ui/Table';
import { StatusPill } from '../components/ui/StatusPill';
import { Modal } from '../components/ui/Modal';
import { shipments } from '../data/shipments';
import { usePagination } from '../hooks/usePagination';
import { useToast } from '../contexts/ToastContext';
import { Shipment } from '../types';
export function Shipments() {
  const navigate = useNavigate();
  const {
    showToast
  } = useToast();
  const [confirming, setConfirming] = useState<Shipment | null>(null);
  const {
    page,
    setPage,
    totalPages,
    pageItems
  } = usePagination(shipments, 25);
  function openShipment(shipment: Shipment) {
    if (shipment.status === 'Shipped') {
      navigate(`/shipments/${shipment.uuid}`);
    } else {
      navigate(`/shipments/${shipment.uuid}/edit`);
    }
  }
  return <>
      <PageHeader title="Shipments" subtitle="View and manage all shipments" actions={<ButtonLink to="/shipments/create" data-id="element-638" data-mp="1gr43yum13v1v">
            <PlusIcon className="h-4 w-4" data-id="element-639" data-mp="1gs5p5rwrdxxb" />
            Create Shipment
          </ButtonLink>} data-id="element-637" data-mp="1lr9z4kribzop" />

      <div className="flex flex-1 flex-col bg-white px-6 py-5" data-id="element-640" data-mp="1bv6zblk4od4x">
        <Table minWidth={980} data-id="element-641" data-mp="29f9rfp4gvy5e">
          <THead data-id="element-642" data-mp="29invd1ss4fey">
            <tr data-id="element-643" data-mp="24hi1v1raoo4d">
              <TH data-id="element-644" data-mp="24gyidglzxro8">Shipment ID</TH>
              <TH data-id="element-645" data-mp="24i04qh8gq0l9">Added Devices</TH>
              <TH data-id="element-646" data-mp="1zqrtrgrhi75z">Created Date</TH>
              <TH data-id="element-647" data-mp="1zpzw80ebtzd2">Return Location</TH>
              <TH data-id="element-648" data-mp="1zoyn41gvfmxz">Current Status</TH>
              <TH data-id="element-649" data-mp="1urydjgktc224">Action</TH>
            </tr>
          </THead>
          <tbody data-id="element-650" data-mp="1urexy6imxpr9">
            {pageItems.map(shipment => <TR key={shipment.id} onClick={() => openShipment(shipment)} data-id="element-651" data-mp="2s846ej23bq9m">
                <TD className="font-semibold text-ink-800" data-id="element-652" data-mp="2ngm1xl0uz7jw">{shipment.id}</TD>
                <TD data-id="element-653" data-mp="2nej44etgsa4m">{shipment.addedDevices}</TD>
                <TD data-id="element-654" data-mp="ljv5e5vv0e0c">{shipment.createdDate}</TD>
                <TD data-id="element-655" data-mp="ll69k6t7vnrz">{shipment.returnLocation}</TD>
                <TD data-id="element-656" data-mp="qgvc8j0j9ksi">
                  <StatusPill status={shipment.status} data-id="element-657" data-mp="qhdd5sy54n6r" />
                </TD>
                <TD data-id="element-658" data-mp="brz82x1lkrx6">
                  {shipment.action === 'none' && <span className="text-ink-400" data-id="element-659" data-mp="gs3o6lylfkmv">-</span>}
                  {shipment.action === 'print-and-ship' && <span className="flex flex-wrap items-center gap-3" data-id="element-660" data-mp="2rswxhtdg416">
                      <button type="button" onClick={e => {
                  e.stopPropagation();
                  showToast('Label Ready', 'Shipping label sent to your downloads');
                }} className="whitespace-nowrap text-[13px] font-medium text-ink-900 hover:underline" data-id="element-661" data-mp="2uotb1pkmkni">
                        Print Shipping Label
                      </button>
                      <Button size="sm" onClick={e => {
                  e?.stopPropagation();
                  setConfirming(shipment);
                }} data-id="element-662" data-mp="1s563zesvc65z">
                        Mark as Shipped
                      </Button>
                    </span>}
                  {shipment.action === 'generate-label' && <button type="button" onClick={e => {
                e.stopPropagation();
                showToast('Label Generated', 'Shipping label is ready to print');
              }} className="whitespace-nowrap text-[13px] font-medium text-ink-900 hover:underline" data-id="element-663" data-mp="3vfiyq4c5emb2">
                      Generate Shipping Label
                    </button>}
                </TD>
              </TR>)}
          </tbody>
        </Table>
        <Pagination page={page} totalPages={totalPages} onChange={setPage} data-id="element-664" data-mp="262xgmzbqtjnk" />
      </div>

      <Modal open={Boolean(confirming)} onClose={() => setConfirming(null)} showClose={false} data-id="element-665" data-mp="x3nxja1lprk3">
        <div className="px-5 py-5 text-center" data-id="element-666" data-mp="12161sn4obilr">
          <h2 className="text-[16px] font-bold text-ink-900" data-id="element-667" data-mp="171kbsapu52mf">Confirm Shipment?</h2>
          <p className="mt-2 text-[13px] text-ink-500" data-id="element-668" data-mp="1b3p3v50mxr6y">
            Once you mark as shipped, you won&apos;t be able to modify this shipment
          </p>
          <div className="mt-5 flex items-center justify-center gap-2" data-id="element-669" data-mp="eezl7ch6oujx">
            <Button variant="secondary" onClick={() => setConfirming(null)} data-id="element-670" data-mp="jbpvfj6b4vxf">
              Cancel
            </Button>
            <Button onClick={() => {
            setConfirming(null);
            showToast('Shipment Updated', 'Shipment has been marked as shipped');
          }} data-id="element-671" data-mp="ndb936qo1e8o">
              Yes, Mark as Shipped
            </Button>
          </div>
        </div>
      </Modal>
    </>;
}